import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { login, type ApiError, type LoginData } from '../services/auth';
import './LoginPage.css';

interface LoginPageProps {
    onLogin: (data: LoginData, email: string) => void;
}

export default function LoginPage({ onLogin }: LoginPageProps) {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
    const [apiError, setApiError] = useState<string | null>(null);

    const validate = () => {
        const newErrors: { email?: string; password?: string } = {};

        if (!email.trim()) {
            newErrors.email = t('login.emailRequired');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            newErrors.email = t('login.emailInvalid');
        }

        if (!password) {
            newErrors.password = t('login.passwordRequired');
        } else if (password.length < 6) {
            newErrors.password = t('login.passwordMinLength');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        setIsLoading(true);
        setApiError(null);

        try {
            const data = await login(email, password);
            onLogin(data, email);
        } catch (err: unknown) {
            const apiErr = err as ApiError;
            setApiError(apiErr.message || t('login.loginFailed'));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="login-container">
            <div className="login-panel">
                <div className="login-form-container">
                    <div className="form-header">
                        <div className="logo">
                            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect width="48" height="48" rx="12" fill="url(#logoGrad)" />
                                <path d="M24 8L36 16V32L24 40L12 32V16L24 8Z" stroke="white" strokeWidth="2" fill="none" />
                                <circle cx="24" cy="22" r="6" fill="white" />
                                <path d="M16 32C16 28.6863 19.5817 26 24 26C28.4183 26 32 28.6863 32 32" stroke="white" strokeWidth="2" fill="none" />
                                <defs>
                                    <linearGradient id="logoGrad" x1="0" y1="0" x2="48" y2="48">
                                        <stop offset="0%" stopColor="#667eea" />
                                        <stop offset="100%" stopColor="#764ba2" />
                                    </linearGradient>
                                </defs>
                            </svg>
                        </div>
                        <h2>{t('login.welcome')}</h2>
                        <p>{t('login.subtitle')}</p>
                    </div>

                    {apiError && (
                        <div className="api-error-banner" role="alert">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                                <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                <circle cx="12" cy="16" r="1" fill="currentColor" />
                            </svg>
                            <span>{apiError}</span>
                        </div>
                    )}

                    <form id="loginForm" className="login-form" onSubmit={handleSubmit} noValidate>
                        <div className="form-group">
                            <label htmlFor="email">{t('login.emailLabel')}</label>
                            <div className={`input-wrapper ${errors.email ? 'error' : ''}`}>
                                <svg className="input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    placeholder={t('login.emailPlaceholder')}
                                    required
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }));
                                    }}
                                />
                            </div>
                            <span className={`error-message ${errors.email ? 'visible' : ''}`} id="emailError">{errors.email}</span>
                        </div>

                        <div className="form-group">
                            <label htmlFor="password">{t('login.passwordLabel')}</label>
                            <div className={`input-wrapper ${errors.password ? 'error' : ''}`}>
                                <svg className="input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="2" />
                                    <path d="M7 11V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    id="password"
                                    name="password"
                                    placeholder={t('login.passwordPlaceholder')}
                                    required
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        if (errors.password) setErrors((prev) => ({ ...prev, password: undefined }));
                                    }}
                                />
                                <button type="button" className="toggle-password" id="togglePassword" aria-label={t('login.togglePassword')} onClick={() => setShowPassword(!showPassword)}>
                                    {showPassword ? (
                                        <svg className="eye-closed" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M17.94 17.94C16.2306 19.243 14.1491 19.9649 12 20C5 20 1 12 1 12C2.24389 9.68192 3.96914 7.65663 6.06 6.06M9.9 4.24C10.5883 4.0789 11.2931 3.99836 12 4C19 4 23 12 23 12C22.393 13.1356 21.6691 14.2048 20.84 15.19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                        </svg>
                                    ) : (
                                        <svg className="eye-open" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                            <span className={`error-message ${errors.password ? 'visible' : ''}`} id="passwordError">{errors.password}</span>
                        </div>

                        <div className="form-options">
                            <label className="remember-me">
                                <input type="checkbox" id="rememberMe" name="rememberMe" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
                                <span className="checkmark"></span>
                                <span>{ t('login.rememberMe')}</span>
                            </label>
                        </div>

                        <button type="submit" className="submit-btn" id="submitBtn" disabled={isLoading}>
                            <span className={`btn-text ${isLoading ? 'hidden' : ''}`}>{t('login.signIn')}</span>
                            <span className={`btn-loader ${isLoading ? '' : 'hidden'}`}>
                                <svg className="spinner" width="20" height="20" viewBox="0 0 24 24">
                                    <circle className="spinner-path" cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="3" />
                                </svg>
                            </span>
                        </button>
                    </form>
                </div>

                <footer className="login-footer">
                    <p>{t('login.copyright')}</p>
                </footer>
            </div>
        </div>
    );
}
